import React, { SyntheticEvent, useContext, useEffect, useState } from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  ListItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";

import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import {
  SubscriptionType,
  User,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import {
  UserSortType,
  UserStatus,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import ConfirmDialog from "../../../shared/confirm-dialog";
import ReasonDropdown from "./shared/reason-dropdown";
import SubscriptionAction from "./subscription-action";
import { GetUsersListParams, getOrgUserList } from "api/endpoints/organisation";
import { downgradeSubscriptionToFree } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import { DEFAULT_SNACKBAR_DURATION, EventTypeReason } from "constants/index";
import { OrgDataContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import useDebouncedEffect from "helpers/hooks/use-debounced-effect";
import {
  DowngradeToFreePlanWrapperProps,
  UrlParamTypes,
} from "types/org-display";

const DowngradeToFreePlan: React.FC<DowngradeToFreePlanWrapperProps> = ({
  refreshOrgData,
}: DowngradeToFreePlanWrapperProps) => {
  const theme = useTheme();
  const { orgData } = useContext(OrgDataContext);
  const { showError } = useContext(SnackbarContext);
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);
  const [isLoading, setIsLoading] = useState(false);

  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [isDialogueOpen, setisDialogueOpen] = useState(false);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const [orgUsersList, setOrgUsersList] = useState<User[]>([]);
  const [requesterUserID, setRequesterUserID] = useState("");
  const [requesterInput, setRequesterInput] = useState("");
  const [inputOrgName, setInputOrgName] = useState("");
  const [orgDowngradeRequestReference, setOrgDowngradeRequestReference] =
    useState("");
  const [inputTotalUsers, setInputTotalUsers] = useState<number | undefined>(
    undefined
  );
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(true);
  const [downgradeReason, setDowngradeReason] = useState<string>("");

  const totalMembers = orgData.inactiveMembers.length + orgData.members.length;
  const orgName = orgData.name;

  const shouldDeactivateUsers = orgData.members.length > 10;

  const isDowngradeToFreePlanDisabled = () => {
    return (
      isSubscriptionActionLoading ||
      orgData?.subscription?.[0]?.type === SubscriptionType.FREE ||
      orgData?.subscription?.[0]?.type === SubscriptionType.FREE_ORG
    );
  };

  useEffect(() => {
    const isConfirmDisabled = () => {
      return (
        inputOrgName !== orgData.name ||
        orgDowngradeRequestReference.length === 0 ||
        inputTotalUsers !== totalMembers ||
        requesterUserID === "" ||
        downgradeReason.length == 0
      );
    };
    setIsConfirmDisabled(isConfirmDisabled());
  }, [
    inputOrgName,
    orgDowngradeRequestReference,
    inputTotalUsers,
    requesterUserID,
    orgData.name,
    totalMembers,
    downgradeReason,
  ]);

  useDebouncedEffect(
    () => {
      searchUser(requesterInput);
    },
    500,
    [requesterInput, setRequesterInput]
  );

  const searchUser = (query: string) => {
    setIsLoading(true);
    const payload: GetUsersListParams = {
      organizationId: roleId,
      pageSize: 100,
      offset: 0,
      sortDirection: SortDirection.ASC,
      sortType: UserSortType.FIRST_NAME,
      query: query,
      userStatus: UserStatus.ACTIVE,
    };

    getOrgUserList(payload, callOptionsHeaders(regionCode, roleId))
      .then((res) => {
        setOrgUsersList(res.users);
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSetRequesterID = (value: string | User | null) => {
    if (!value) {
      return;
    }
    if (typeof value === "string") {
      return setRequesterUserID(value);
    }
    if (value && typeof value === "object") {
      return setRequesterUserID(value.id);
    }
  };

  const renderRequesterInput = () => {
    return (
      <Autocomplete
        autoHighlight
        autoComplete
        options={orgUsersList}
        loading={isLoading}
        data-testid={"downgrade-to-free-requester-field"}
        noOptionsText="User not found"
        onChange={(_event, value, reason) => {
          if (reason === "clear") {
            setRequesterUserID("");
          }
          if (reason === "selectOption") {
            handleSetRequesterID(value);
          }
        }}
        onInputChange={(_event, value) => {
          setRequesterInput(value);
        }}
        getOptionLabel={(option: User) =>
          `${option.firstName} ${option.lastName} - ${option.email}`
        }
        renderOption={(props, option) => (
          <li {...props}>
            <Box>
              <Typography>
                {option.firstName} {option.lastName}{" "}
              </Typography>
              <Typography variant="caption">{option.email}</Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            label="Requester"
            name="requester"
            variant="outlined"
            margin="normal"
            inputProps={{
              ...params.inputProps,
              "data-testid": "downgrade-to-free-requester-value",
            }}
          />
        )}
      />
    );
  };

  const downgradeToFreePlan = () => {
    setShowLoadingSpinner(true);
    setIsSubscriptionActionLoading(true);

    downgradeSubscriptionToFree(
      {
        orgId: roleId,
        userId: requesterUserID,
        downgradeReason,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then(() => {
        setOpenSuccessAlert(true);
        refreshOrgData();
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
        setIsSubscriptionActionLoading(false);
      });
  };

  const cancelChanges = () => {
    setisDialogueOpen(false);
    setRequesterUserID("");
  };

  const handleSnackbarClose = (
    _event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  const warnContainerSx = {
    background: "#ffd4d4",
    border: "1px solid #941c1c",
    color: "#941c1c",
    borderRadius: 1,
    padding: theme.spacing(1.25),
    margin: "6px 4px 0px",
  };

  return (
    <React.Fragment>
      <SubscriptionAction title={"Downgrade to Free Plan"}>
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            data-testid="downgrade-to-free-plan-btn"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isDowngradeToFreePlanDisabled()}
            onClick={() => setisDialogueOpen(true)}
          >
            Downgrade
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title={"Downgrade to Free Plan"}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        width="md"
        open={isDialogueOpen}
        setOpen={setisDialogueOpen}
        onConfirm={() => {
          setisDialogueOpen(false);
          downgradeToFreePlan();
        }}
        disabled={isConfirmDisabled}
        onCancel={() => {
          cancelChanges();
        }}
      >
        <Grid container spacing={1}>
          <Grid
            item
            xs={12}
            style={{ marginTop: "15px", marginBottom: "10px" }}
          >
            <Box width={1} sx={warnContainerSx}>
              <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                This organisation will be downgraded to a Free Plan.
              </ListItem>
              <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                Up to 10 users can continue to use SafetyCulture for free and
                access our Free Plan features.
              </ListItem>
              {shouldDeactivateUsers && (
                <>
                  <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                    <b>
                      All other users other than the requester will be
                      deactivated.
                    </b>
                  </ListItem>
                  <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                    Deactivating users may take some time depending on the size
                    of the organization.
                  </ListItem>
                  <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                    Please check that the organization has{" "}
                    <b>less than 10 users</b> after and report any issues to the
                    #business-systems channel.
                  </ListItem>
                </>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box>
              <Typography variant="body1">
                Provide the link to the customer request
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="customerDowngradeRequest-input"
              label="Link to customer request"
              value={orgDowngradeRequestReference}
              onChange={(event) => {
                setOrgDowngradeRequestReference(event.target.value.trim());
              }}
              error={orgDowngradeRequestReference.length === 0}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{
                "data-anchor": "do-customerDowngradeRequest-input",
                "data-testid": "do-customerDowngradeRequest-input",
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography variant="body1">
                Type the name of the organisation to confirm the downgrade
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="orgName-input"
              label="Organisation Name"
              value={inputOrgName}
              onChange={(event) => {
                setInputOrgName(event.target.value);
              }}
              error={inputOrgName !== orgName}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{
                "data-anchor": "do-name-input",
                "data-testid": "do-name-input",
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography variant="body1">
                Type the total number of users in the organisation to confirm
                the downgrade
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="orgName-input"
              label="Total organisation members (active + deactivated)"
              type="number"
              value={inputTotalUsers}
              onChange={(event) => {
                const inputValue =
                  event.target.value === ""
                    ? undefined
                    : Number(event.target.value);
                setInputTotalUsers(inputValue);
              }}
              error={inputTotalUsers != totalMembers}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{
                "data-anchor": "do-total-user-input",
                "data-testid": "do-total-user-input",
              }}
            />
          </Grid>
          <Grid item xs={12}>
            {renderRequesterInput()}
          </Grid>
          <Grid item xs={12}>
            <ReasonDropdown
              value={downgradeReason}
              onChange={setDowngradeReason}
              label="Downgrade reason*"
              id="downgrade-select"
              labelId="downgrade-select-label"
              dataAnchorPrefix="downgrade"
              dataTestIdPrefix="downgrade"
            />
          </Grid>
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleSnackbarClose}
      >
        <Alert severity="success" variant="filled">
          Successfully downgraded to Free Plan
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default DowngradeToFreePlan;
